import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { EMPTY_MANUAL_RECEIPT, manualReceiptToApi, normalizeReceiptDate, normalizeReceiptTime, validateManualReceipt, type ManualField, type ManualFieldErrors, type ManualReceiptFields } from './receiptManualValidation';
import { isReceiptAlreadyUsedCode, RECEIPT_ALREADY_USED_TEXT } from './ReceiptRejectionNotice';
import './receipt-alternatives.css';

type InputMode = 'photo' | 'manual';
type Phase = 'manual' | 'photo' | 'uploading' | 'verifying' | 'preview' | 'confirming' | 'failure' | 'pending' | 'auth-required';

type SafePreview = {
  id: string;
  source: InputMode;
  status: string;
  error_code?: string | null;
  expires_at?: string | null;
  receipt?: {
    total_amount: string | null;
    currency: string | null;
    purchase_local_timestamp: string | null;
    merchant_name: string | null;
  };
  scan_id?: string;
};

type ReceiptAlternativeFlowProps = {
  initialMode: InputMode;
  initialPhoto?: File | null;
  onBack: () => void;
  onScanCreated: (scanId: string) => void;
  onReceiptAlreadyUsed: (receipt: SafePreview['receipt'] | null) => void;
};

class PreviewApiError extends Error {
  constructor(public readonly code: string, public readonly status: number) {
    super(code);
  }
}

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const POLL_INTERVAL_MS = 2500;
const MAX_AUTO_POLLS = 24;
const SOURCE_HELP: Record<'fn' | 'fd' | 'fp', string> = {
  fn: 'Найдите на чеке строку «ФН». Обычно это 16 цифр.',
  fd: 'Номер рядом с обозначением «ФД» или «Фискальный документ».',
  fp: 'Найдите «ФП», «ФПД» или «Фискальный признак документа».',
};
const INPUT_LABELS: Record<ManualField, string> = {
  date: 'Дата покупки',
  time: 'Время покупки',
  amount: 'Итого по чеку',
  fn: 'ФН — фискальный накопитель',
  fd: 'ФД — номер документа',
  fp: 'ФП / ФПД — фискальный признак',
  operation: 'Тип операции',
};

function newRequestKey(): string {
  return crypto.randomUUID();
}

function errorText(code: string, source: InputMode): string {
  switch (code) {
    case 'recognition_failed':
      return 'QR-код на фотографии не распознан. Фото может быть слишком маленьким или нечётким. Сделайте новый снимок чека крупнее, без размытия, или введите реквизиты вручную.';
    case 'ambiguous_fiscal_data':
      return 'На фотографии найдено несколько несовпадающих фискальных QR. Загрузите один полный чек.';
    case 'processing_timeout':
      return 'Распознавание фото заняло слишком много времени. Попробуйте более чёткий снимок или ручной ввод.';
    case 'processing_failed':
      return 'Сервер не смог обработать фотографию. Это техническая ошибка распознавания, а не отказ в проверке фискального чека. Попробуйте позднее.';
    case 'receipt_photo_busy':
    case 'temporarily_busy':
      return 'Сейчас много чеков на проверке. Попробуйте позже или введите данные вручную.';
    case 'provider_rejected':
    case 'amount_mismatch':
    case 'receipt_fields_conflict':
      return 'Проверка не подтвердила данные чека. Проверьте реквизиты и попробуйте снова.';
    case 'provider_unavailable':
    case 'rate_limited':
      return 'Сервис проверки чеков временно недоступен. Повторите проверку позже.';
    case 'already_used':
    case 'receipt_already_used':
      return RECEIPT_ALREADY_USED_TEXT;
    case 'preview_expired':
      return 'Время подтверждения истекло. Проверьте чек ещё раз.';
    case 'feature_disabled':
      return 'Этот способ проверки сейчас недоступен. Можно воспользоваться камерой.';
    case 'idempotency_conflict':
      return 'Данные запроса изменились. Начните новую проверку.';
    case 'unauthorized':
      return 'Для загрузки фотографии и проверки реквизитов сначала войдите в Хоппер через Яндекс.';
    case 'csrf_expired':
      return 'Сессия устарела. Обновите страницу, затем повторите отправку.';
    case 'unsupported_photo':
      return 'Нужно фото одного полного чека в JPEG или PNG размером до 15 МБ.';
    case 'photo_too_large':
      return 'Фотография превышает 15 МБ. Выберите файл поменьше.';
    case 'consent_required':
      return 'Примите политику конфиденциальности, затем повторите проверку.';
    case 'invalid_verified_preview':
      return 'Не удалось подтвердить проверку. Начните проверку чека заново.';
    case 'request_failed':
      return 'Не удалось соединиться с сервером. Проверьте интернет и запросите статус ещё раз.';
    default:
      return source === 'photo'
        ? 'Не удалось проверить фотографию чека. Попробуйте другое фото или ручной ввод.'
        : 'Не удалось проверить чек. Проверьте реквизиты и попробуйте снова.';
  }
}

async function parseResponse(response: Response): Promise<{ preview?: SafePreview; scan?: { id?: string }; error?: { code?: string }; errors?: Record<string, string[]>; csrf_token?: string; authenticated?: boolean }> {
  return response.json().catch(() => ({})) as Promise<{ preview?: SafePreview; scan?: { id?: string }; error?: { code?: string }; errors?: Record<string, string[]>; csrf_token?: string; authenticated?: boolean }>;
}

async function requestJson(url: string, init: RequestInit = {}, timeoutMs = 14000) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...init,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(init.headers || {}) },
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      const code = response.status === 401 ? 'unauthorized'
        : data.error?.code || (response.status === 419 ? 'csrf_expired' : response.status === 429 ? 'rate_limited' : 'request_failed');
      const error = new PreviewApiError(code, response.status);
      (error as PreviewApiError & { errors?: Record<string, string[]> }).errors = data.errors;
      throw error;
    }
    return data;
  } finally {
    window.clearTimeout(timer);
  }
}

async function csrfToken() {
  const data = await requestJson('/api/v1/auth/csrf');
  const token = data.csrf_token;
  if (!token) throw new PreviewApiError('csrf_expired', 419);
  return token;
}

async function ensureSession() {
  const data = await requestJson('/api/v1/auth/session');
  if (!data.authenticated) throw new PreviewApiError('unauthorized', 401);
}

function uploadImage(file: File, csrf: string, key: string, onProgress: (value: number) => void, signal: AbortSignal): Promise<SafePreview> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v1/receipt-previews/photo');
    xhr.responseType = 'json';
    xhr.timeout = 40000;
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-CSRF-TOKEN', csrf);
    xhr.setRequestHeader('Idempotency-Key', key);
    const stop = () => xhr.abort();
    signal.addEventListener('abort', stop, { once: true });
    const finish = () => signal.removeEventListener('abort', stop);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.min(99, Math.round(event.loaded / event.total * 100)));
    };
    xhr.onload = () => {
      finish();
      const data = xhr.response as { preview?: SafePreview; error?: { code?: string } } | null;
      if (xhr.status >= 200 && xhr.status < 300 && data?.preview?.id) {
        resolve(data.preview);
      } else {
        reject(new PreviewApiError(xhr.status === 401 ? 'unauthorized'
          : data?.error?.code || (xhr.status === 413 ? 'photo_too_large'
            : xhr.status === 403 ? 'consent_required'
            : xhr.status === 429 ? 'rate_limited' : 'request_failed'), xhr.status));
      }
    };
    xhr.onerror = () => { finish(); reject(new PreviewApiError('request_failed', 0)); };
    xhr.ontimeout = () => { finish(); reject(new PreviewApiError('request_failed', 0)); };
    xhr.onabort = () => { finish(); reject(new PreviewApiError('request_failed', 0)); };
    const form = new FormData();
    form.append('photo', file, file.name);
    xhr.send(form);
  });
}

export function ReceiptAlternativeFlow({ initialMode, initialPhoto, onBack, onScanCreated, onReceiptAlreadyUsed }: ReceiptAlternativeFlowProps) {
  const [mode, setMode] = useState<InputMode>(initialMode);
  const [phase, setPhase] = useState<Phase>(initialMode);
  const [fields, setFields] = useState<ManualReceiptFields>({ ...EMPTY_MANUAL_RECEIPT });
  const [errors, setErrors] = useState<ManualFieldErrors>({});
  const [help, setHelp] = useState<'fn' | 'fd' | 'fp' | null>(null);
  const [preview, setPreview] = useState<SafePreview | null>(null);
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [busy, setBusy] = useState(false);
  const [retrySource, setRetrySource] = useState<InputMode>(initialMode);
  const [selectedFile, setSelectedFile] = useState<File | null>(initialPhoto ?? null);
  const [pendingChecks, setPendingChecks] = useState(0);
  const mounted = useRef(true);
  const uploadController = useRef<AbortController | null>(null);
  const requestKey = useRef(newRequestKey());
  const confirmKey = useRef(newRequestKey());
  const initialStarted = useRef(false);
  const active = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      uploadController.current?.abort();
    };
  }, []);

  const showError = useCallback((error: unknown, fallback: InputMode, receipt: SafePreview['receipt'] | null = null) => {
    if (!mounted.current) return;
    const code = error instanceof PreviewApiError ? error.code : 'request_failed';
    if (isReceiptAlreadyUsedCode(code)) {
      // Duplicate is a resolved fiscal result, not a QR/read or provider failure.
      onReceiptAlreadyUsed(receipt);
      return;
    }
    const validation = error as PreviewApiError & { errors?: Record<string, string[]> };
    if (validation?.errors && fallback === 'manual') {
      const mapped: ManualFieldErrors = {};
      for (const key of Object.keys(INPUT_LABELS) as ManualField[]) {
        if (validation.errors[key]) mapped[key] = validation.errors[key][0];
      }
      if (validation.errors.receipt) mapped.fn = 'Проверьте реквизиты фискального чека.';
      setErrors(mapped);
    }
    setErrorCode(code);
    setMessage(errorText(code, fallback));
    setRetrySource(fallback);
    setPhase(code === 'unauthorized' ? 'auth-required' : 'failure');
    setBusy(false);
  }, [onReceiptAlreadyUsed]);

  const acceptPreview = useCallback((candidate: SafePreview, nextMode: InputMode) => {
    if (!mounted.current) return;
    if (isReceiptAlreadyUsedCode(candidate.error_code ?? candidate.status)) {
      onReceiptAlreadyUsed(candidate.receipt ?? null);
      return;
    }
    setMode(nextMode);
    setPreview(candidate);
    setPendingChecks(0);
    if (candidate.status === 'confirmed' && candidate.scan_id) {
      onScanCreated(candidate.scan_id);
      return;
    }
    setPhase(candidate.status === 'ready_for_confirmation' ? 'preview' : 'verifying');
  }, [onScanCreated, onReceiptAlreadyUsed]);

  const sendPhoto = useCallback(async (file: File) => {
    if (active.current) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > MAX_FILE_BYTES || file.size === 0) {
      showError(new PreviewApiError('unsupported_photo', 422), 'photo');
      setMessage('Выберите одно фото полного чека в JPEG или PNG до 15 МБ.');
      return;
    }
    active.current = true;
    setBusy(true);
    setMode('photo');
    setSelectedFile(file);
    setPreview(null);
    setUploadPercent(0);
    setPhase('uploading');
    setMessage('');
    const controller = new AbortController();
    uploadController.current = controller;
    try {
      await ensureSession();
      const csrf = await csrfToken();
      if (!mounted.current || controller.signal.aborted) return;
      const candidate = await uploadImage(file, csrf, requestKey.current,
        (progress) => { if (mounted.current) setUploadPercent(progress); }, controller.signal);
      if (!controller.signal.aborted) acceptPreview(candidate, 'photo');
    } catch (error) {
      if (!controller.signal.aborted) showError(error, 'photo');
    } finally {
      active.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [acceptPreview, showError]);

  useEffect(() => {
    if (initialPhoto && !initialStarted.current) {
      initialStarted.current = true;
      void sendPhoto(initialPhoto);
    }
  }, [initialPhoto, sendPhoto]);

  const selectPhoto = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file || active.current) return;
    requestKey.current = newRequestKey();
    confirmKey.current = newRequestKey();
    void sendPhoto(file);
  }, [sendPhoto]);

  const switchManual = useCallback(() => {
    if (active.current) return;
    setMode('manual');
    setPhase('manual');
    setPreview(null);
    setMessage('');
    setErrorCode('');
    setErrors({});
    requestKey.current = newRequestKey();
    confirmKey.current = newRequestKey();
  }, []);

  const updateField = (name: ManualField, value: string) => {
    setFields((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
    setPreview(null);
    requestKey.current = newRequestKey();
    confirmKey.current = newRequestKey();
  };

  const submitManual = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (active.current) return;
    const issues = validateManualReceipt(fields);
    setErrors(issues);
    const body = manualReceiptToApi(fields);
    if (!body || Object.keys(issues).length) return;
    active.current = true;
    setBusy(true);
    setMessage('');
    setMode('manual');
    setPhase('verifying');
    setPreview(null);
    try {
      await ensureSession();
      const csrf = await csrfToken();
      const response = await requestJson('/api/v1/receipt-previews/manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf,
          'Idempotency-Key': requestKey.current,
        },
        body: JSON.stringify(body),
      }, 30000);
      if (!response.preview?.id) throw new PreviewApiError('request_failed', 0);
      acceptPreview(response.preview, 'manual');
    } catch (error) {
      showError(error, 'manual');
    } finally {
      active.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  useEffect(() => {
    if (phase !== 'verifying' || !preview?.id) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await requestJson('/api/v1/receipt-previews/' + encodeURIComponent(preview.id));
        if (cancelled || !mounted.current) return;
        const next = response.preview;
        if (!next) throw new PreviewApiError('request_failed', 0);
        setPreview(next);
        if (next.status === 'ready_for_confirmation') {
          setPhase('preview');
          return;
        }
        if (next.status === 'confirmed' && next.scan_id) {
          onScanCreated(next.scan_id);
          return;
        }
        if (['queued', 'recognizing', 'provider_verifying'].includes(next.status)) {
          setPendingChecks((count) => count + 1);
          return;
        }
        showError(new PreviewApiError(next.error_code || next.status, 422), mode, next.receipt ?? null);
      } catch (error) {
        if (!cancelled) showError(error, mode);
      }
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [phase, preview?.id, pendingChecks, mode, onScanCreated, showError]);

  useEffect(() => {
    if (phase === 'verifying' && pendingChecks >= MAX_AUTO_POLLS) setPhase('pending');
  }, [phase, pendingChecks]);

  const checkStatus = async () => {
    if (!preview?.id || active.current) return;
    active.current = true;
    setBusy(true);
    try {
      const result = await requestJson('/api/v1/receipt-previews/' + encodeURIComponent(preview.id));
      if (!result.preview) throw new PreviewApiError('request_failed', 0);
      if (isReceiptAlreadyUsedCode(result.preview.error_code ?? result.preview.status)) {
        onReceiptAlreadyUsed(result.preview.receipt ?? null);
        return;
      }
      acceptPreview(result.preview, mode);
      if (!['ready_for_confirmation', 'confirmed', 'queued', 'recognizing', 'provider_verifying'].includes(result.preview.status)) {
        showError(new PreviewApiError(result.preview.error_code || result.preview.status, 422), mode);
      }
    } catch (error) {
      showError(error, mode);
    } finally {
      active.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const confirmPreview = async () => {
    if (!preview?.id || active.current) return;
    active.current = true;
    setBusy(true);
    setPhase('confirming');
    try {
      const csrf = await csrfToken();
      const response = await requestJson('/api/v1/receipt-previews/' + encodeURIComponent(preview.id) + '/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, 'Idempotency-Key': confirmKey.current },
        body: JSON.stringify({}),
      }, 30000);
      const id = response.scan?.id;
      if (!id) throw new PreviewApiError('request_failed', 0);
      onScanCreated(id);
    } catch (error) {
      if (error instanceof PreviewApiError && error.code === 'request_failed') {
        // Outcome may have committed; retain the SAME preview and SAME confirm key.
        // Poll safe status rather than generate another paid verification.
        setMessage('Связь прервалась. Подтверждение могло сохраниться — проверьте статус.');
        setPhase('pending');
      } else {
        showError(error, mode);
      }
    } finally {
      active.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const editManual = () => {
    setPhase('manual');
    setPreview(null);
    setErrors({});
    setMessage('');
    setErrorCode('');
    // New attempt only after explicit user click and actual payload validation.
    requestKey.current = newRequestKey();
    confirmKey.current = newRequestKey();
  };

  const manualEntry = () => (
    <form className="receipt-alt-form" onSubmit={(event) => void submitManual(event)} noValidate>
      <h2>Ввести данные чека</h2>
      <p>Все данные напечатаны на фискальном чеке. Мы проверим их перед подтверждением визита.</p>
      {(['date', 'time', 'amount', 'fn', 'fd', 'fp'] as ManualField[]).map((name) => (
        <div className="receipt-alt-field" key={name}>
          <div className="receipt-alt-field-heading">
            <label htmlFor={'receipt-alt-' + name}>{INPUT_LABELS[name]}</label>
            {['fn', 'fd', 'fp'].includes(name) && (
              <button type="button" className="receipt-alt-help-button"
                aria-label={'Подсказка для ' + INPUT_LABELS[name]}
                aria-expanded={help === name} onClick={() => setHelp((previous) => previous === name ? null : name as 'fn' | 'fd' | 'fp')}>?</button>
            )}
          </div>
          {help === name && name in SOURCE_HELP && <p className="receipt-alt-help" role="note">{SOURCE_HELP[name as 'fn' | 'fd' | 'fp']}</p>}
          <div className="receipt-alt-control">
            <input
              id={'receipt-alt-' + name}
              type="text"
              inputMode={name === 'amount' ? 'decimal' : 'numeric'}
              autoComplete="off"
              spellCheck={false}
              value={fields[name]}
              placeholder={({ date: 'ДД.ММ.ГГГГ', time: 'ЧЧ:ММ', amount: '1785,50', fn: '16 цифр', fd: 'Номер ФД', fp: 'Номер ФП/ФПД' } as Partial<Record<ManualField, string>>)[name]}
              maxLength={name === 'fn' ? 16 : name === 'fd' || name === 'fp' ? 10 : name === 'date' ? 10 : name === 'time' ? 5 : 32}
              aria-invalid={Boolean(errors[name])}
              aria-describedby={errors[name] ? 'receipt-alt-error-' + name : undefined}
              onChange={(event) => updateField(name, event.target.value)}
              onBlur={() => {
                const normalized = name === 'date' ? normalizeReceiptDate(fields.date)
                  : name === 'time' ? normalizeReceiptTime(fields.time) : fields[name];
                if (normalized !== fields[name]) updateField(name, normalized);
                if (normalized.trim()) {
                  const values = { ...fields, [name]: normalized };
                  setErrors((previous) => ({ ...previous, [name]: validateManualReceipt(values)[name] }));
                }
              }}
              onFocus={(event) => {
                const element = event.currentTarget;
                window.setTimeout(() => element.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 250);
              }}
            />
            {name === 'amount' && <span className="receipt-alt-currency">₽</span>}
          </div>
          {errors[name] && <span id={'receipt-alt-error-' + name} className="receipt-alt-invalid" role="alert">{errors[name]}</span>}
        </div>
      ))}
      <div className="receipt-alt-field">
        <label htmlFor="receipt-alt-operation">Тип операции</label>
        <select id="receipt-alt-operation" value={fields.operation}
          aria-invalid={Boolean(errors.operation)}
          onChange={(event) => updateField('operation', event.target.value)}>
          <option value="">Выберите тип операции</option>
          <option value="1">Приход — обычная покупка</option>
          <option value="2">Возврат прихода</option>
          <option value="3">Расход</option>
          <option value="4">Возврат расхода</option>
        </select>
        {errors.operation && <span className="receipt-alt-invalid" role="alert">{errors.operation}</span>}
      </div>
      <button type="submit" className="scanner-primary-button" disabled={busy || Object.keys(validateManualReceipt(fields)).length > 0}>
        {busy ? 'Проверяем чек…' : 'Проверить чек'}
      </button>
      <p className="receipt-alt-footnote">Проверка не означает начисление XP. Визит засчитывается только после подтверждения и действующих проверок Хоппера.</p>
    </form>
  );

  return (
    <div className="scanner-sheet receipt-alt-sheet">
      <div className="receipt-alt-backline">
        <button type="button" className="scanner-text-button" onClick={onBack} disabled={busy}>← К камере</button>
        <span>{mode === 'photo' ? 'Фото чека' : 'Ручной ввод'}</span>
      </div>
      {phase === 'auth-required' && (
        <div className="receipt-alt-status">
          <span className="scanner-kicker">Нужен вход</span>
          <h2>Сначала войдите в Хоппер</h2>
          <p>Чтобы загрузить фото или проверить реквизиты чека, войдите через Яндекс. Затем откройте сканер снова.</p>
          <button type="button" className="scanner-primary-button"
            onClick={() => window.location.assign('/api/v1/auth/yandex/redirect')}>Войти через Яндекс</button>
          <button type="button" className="scanner-text-button" onClick={onBack}>Вернуться к сканеру</button>
        </div>
      )}
      {phase === 'manual' && manualEntry()}
      {phase === 'photo' && (
        <div className="receipt-alt-status">
          <h2>Выбрать фото чека</h2>
          <p>Загрузите одно фото полного фискального чека. Хоппер найдёт QR-код на сервере и проверит чек.</p>
          <button type="button" className="scanner-primary-button" onClick={() => fileInput.current?.click()}>
            Выбрать фото чека
          </button>
          <button type="button" className="scanner-text-button" onClick={switchManual}>Ввести данные вручную</button>
        </div>
      )}
      <input ref={fileInput} type="file" accept="image/jpeg,image/png" className="receipt-alt-hidden-file"
        aria-label="Выбрать фото одного полного чека" onChange={selectPhoto} />
      {(phase === 'uploading' || phase === 'verifying' || phase === 'confirming') && (
        <div className="receipt-alt-status" role="status" aria-live="polite">
          <div className="scanner-progress-ring"><span /></div>
          <h2>{phase === 'uploading' ? 'Загружаем фото чека' : phase === 'verifying' ? 'Проверяем чек' : 'Подтверждаем визит'}</h2>
          <p>{phase === 'uploading' ? 'Загружаем изображение в защищённое хранилище.' : phase === 'verifying'
            ? 'Ищем фискальный QR и проверяем его. Это ещё не подтверждённый визит.'
            : 'Сохраняем результат и запускаем действующие проверки визита.'}</p>
          {phase === 'uploading' && <progress max={100} value={uploadPercent} aria-label="Загрузка фотографии">{uploadPercent}%</progress>}
        </div>
      )}
      {phase === 'preview' && preview?.receipt && (
        <div className="receipt-alt-status">
          <span className="scanner-kicker">Проверено фискальным провайдером</span>
          <h2>Проверьте данные чека</h2>
          <div className="receipt-alt-summary">
            <div><span>Сумма</span><strong>{preview.receipt.total_amount} ₽</strong></div>
            <div><span>Дата и время покупки</span><strong>{preview.receipt.purchase_local_timestamp?.replace(' ', ', ') ?? 'По чеку'}</strong></div>
            <div><span>Заведение</span><strong>{preview.receipt.merchant_name || 'Определим после подтверждения'}</strong></div>
          </div>
          <p>Чек проверен, но визит и XP ещё не засчитаны. Подтвердите отправку чека в Хоппер.</p>
          <button type="button" className="scanner-primary-button" onClick={() => void confirmPreview()} disabled={busy}>
            Подтвердить визит
          </button>
          {mode === 'manual' && <button type="button" className="scanner-text-button" onClick={editManual}>Исправить данные</button>}
        </div>
      )}
      {phase === 'pending' && (
        <div className="receipt-alt-status">
          <h2>Чек ещё обрабатывается</h2>
          <p>{message || 'Проверка продолжается на сервере. Не отправляйте тот же чек повторно.'}</p>
          <button type="button" className="scanner-primary-button" disabled={busy} onClick={() => void checkStatus()}>Проверить статус</button>
          {mode === 'manual' && <button type="button" className="scanner-text-button" onClick={editManual}>Изменить реквизиты</button>}
        </div>
      )}
      {phase === 'failure' && (
        <div className="receipt-alt-status" role="alert">
          <h2>{mode === 'photo' && ['recognition_failed', 'ambiguous_fiscal_data', 'processing_timeout'].includes(errorCode)
            ? 'Не удалось прочитать QR-код на чеке'
            : errorCode === 'processing_failed' ? 'Ошибка обработки фотографии'
            : ['provider_rejected', 'amount_mismatch', 'receipt_fields_conflict'].includes(errorCode)
              ? 'Фискальная проверка не пройдена' : 'Не удалось проверить чек'}</h2>
          <p>{message}</p>
          {mode === 'photo' && (
            <button type="button" className="scanner-primary-button" onClick={() => fileInput.current?.click()}>Выбрать другое фото</button>
          )}
          {retrySource === 'manual' && (
            <button type="button" className="scanner-primary-button" onClick={editManual}>Исправить данные</button>
          )}
          <button type="button" className="scanner-text-button" onClick={switchManual}>Ввести данные вручную</button>
          {errorCode === 'unauthorized' && (
            <button type="button" className="scanner-primary-button"
              onClick={() => window.location.assign('/api/v1/auth/yandex/redirect')}>Войти через Яндекс</button>
          )}
          {selectedFile && <p className="receipt-alt-footnote">Фотография не сохраняется в браузере после закрытия формы.</p>}
        </div>
      )}
    </div>
  );
}
